import { describeContentError } from '../../api/describeQueryError.api';
import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { LoadingSkeleton } from '../LoadingSkeleton';
import type { ContentStateProps } from './types';

/**
 * The three things a page shows instead of its content: loading, failed, empty.
 * None of them replaces the chrome — a reader who hits a 404 three levels down
 * can still see where they were.
 *
 * **Loading and empty are the caller's shapes**, which is what keeps this one
 * component rather than four.
 *
 * **The order is load-bearing: error beats empty.** A failed request leaves
 * `data` undefined, which every caller's `isEmpty` reads as empty, so checking
 * empty first reports a 500 as "nothing here yet".
 *
 * **The empty state is unreachable against today's content** and built anyway:
 * the scheduler can write an incomplete tree, and the alternative is a blank
 * page.
 */
export const ContentState = ({
  isLoading,
  error,
  onRetry,
  errorTitle = 'Unable to load this content',
  errorNoun = 'content',
  isEmpty = false,
  emptyTitle = 'Nothing here yet',
  emptyMessage = 'This part of the catalog has not been written yet. It will appear here as the toolkit grows.',
  skeleton,
  children,
}: ContentStateProps) => {
  if (isLoading) {
    return skeleton ?? <LoadingSkeleton layout="phase" label="Loading content" />;
  }

  if (error !== undefined) {
    return (
      <ErrorState
        title={errorTitle}
        message={describeContentError(error, errorNoun)}
        onRetry={onRetry}
      />
    );
  }

  if (isEmpty) {
    return <EmptyState title={emptyTitle} message={emptyMessage} />;
  }

  return children;
};
